import { NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { BaseService, findControlByName, GetFieldValuePipe } from '@shared/core';
import { LocalizationPipe } from '@shared/modules/localization';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { catchError, map, Observable, of } from 'rxjs';
import { UILoading } from '../../../loading';
import {
  FormFieldSelectConfigType,
  IFormFieldServerSelectPaginationType,
  IFormFieldServerSelectRequestParamsConfig,
} from '../../models';
import { BaseFormFieldComponent, baseProviderComponent } from '../base-form-field.component';
import { UIIcon } from '../../../icon';

type IFormSelectFilter = IFormSelectPageFilter | IFormSelectOffsetPageFilter;

interface IFormSelectPageFilter {
  [key: string]: unknown;
  page_index: number;
  page_size: number;
}

interface IFormSelectOffsetPageFilter {
  [key: string]: unknown;
  offset: number;
  limit: number;
}

@Component({
  selector: 'ui-form-select',
  templateUrl: './form-select.component.html',
  styleUrl: './form-select.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgIf,
    NgStyle,
    NzFormModule,
    NzSelectModule,
    FormsModule,
    GetFieldValuePipe,
    LocalizationPipe,
    ReactiveFormsModule,
    UILoading,
    UIIcon,
  ],
  providers: [...baseProviderComponent(UIFormSelect), GetFieldValuePipe],
})
export class UIFormSelect extends BaseFormFieldComponent<unknown | unknown[]> implements OnInit {
  @Input() override set config(configValue: FormFieldSelectConfigType) {
    this.label = configValue.label;
    this.placeholder = configValue.placeholder ?? '';
    this.showSearch = configValue.showSearch;
    this.customOptionFields = configValue.customOptionFields ?? [];
    this.multiple = configValue.multiple;
    this.hideLabel = configValue.hideLabel ?? false;
    this.optionIconField = configValue.optionIconField;

    if ('requestUrl' in configValue) {
      this.serverSearch = !!configValue.searchField;
      this.searchField = configValue.searchField;
      this.requestMethod = configValue.requestMethod ?? 'get';
      this.requestUrl = configValue.requestUrl;
      this.requestParams = configValue.requestParams;
      this.responseMapper = configValue.responseMapper;
      this.pageable = configValue.pageable;
      this.paginationType = configValue.paginationType ?? 'page';
    } else {
      this.serverSearch = false;
      this.options = configValue.options;
      this.definedOptions = configValue.options;
    }

    this.optionLabelField = configValue.optionLabelField ?? '';
    this.optionValueField = configValue.optionValueField ?? '';
  }

  @Input() requestMethod = 'get';
  @Input() requestUrl?: string;
  @Input() requestParams?: IFormFieldServerSelectRequestParamsConfig;
  @Input() responseMapper?: (response: any) => any;
  @Input() pageable?: boolean;
  @Input() paginationType?: IFormFieldServerSelectPaginationType = 'page';
  @Input() options?: Array<any> = [];
  @Input() definedOptions?: Array<any> = [];
  @Input() optionLabelField?: string;
  @Input() optionValueField?: string;
  @Input() showSearch?: boolean;
  @Input() customOptionFields?: string[] = [];
  @Input() serverSearch?: boolean;
  @Input() searchField?: string;
  @Input() multiple?: boolean;
  @Input() hideMargin?: boolean;
  @Input() optionIconField?: string;

  isLoadingData = false;
  isLoadingMore = false;

  private optionFilter?: IFormSelectFilter;

  private getFieldValuePipe = inject(GetFieldValuePipe);
  private httpService = inject(BaseService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = inject(DestroyRef);

  get selectMode() {
    return this.multiple ? 'multiple' : 'default';
  }

  get isCustomContent(): boolean {
    return this.customOptionFields != null && this.customOptionFields.length > 0;
  }

  // ControlValueAccessor methods
  override writeValue(value: any): void {
    if (value == null) {
      this.value = value;
      return;
    }

    if (typeof value !== 'object') {
      this.value = value;
      this.options = [value];
      return;
    }

    if (Array.isArray(value)) {
      this.options = value;
      this.onValueChange(
        value.map(item => this.getFieldValuePipe?.transform(item, this.optionValueField)),
      );
      return;
    }

    const newValue = this.getFieldValuePipe?.transform(value, this.optionValueField);
    this.options = [value];
    this.onValueChange(newValue);
  }

  private initOptionFilter(): IFormSelectFilter {
    return this.paginationType === 'page'
      ? {
          page_index: 1,
          page_size: 20,
        }
      : {
          limit: 20,
          offset: 0,
        };
  }

  ngOnInit(): void {
    this.optionFilter = this.initOptionFilter();

    if (!this.requestParams) {
      return;
    }
    Object.entries(this.requestParams)?.forEach(([paramName, paramConfig]) => {
      const relatedControl = findControlByName(paramConfig.relatedFieldName, this.parentFormGroup);
      if (!relatedControl) {
        return;
      }

      if (!this.optionFilter) {
        this.optionFilter = this.initOptionFilter();
      }

      this.optionFilter[paramName] = paramConfig.valueMapper
        ? paramConfig.valueMapper(relatedControl.value)
        : relatedControl.value;
      if (relatedControl.value == null || relatedControl.value === '') {
        if (paramConfig.required) {
          this.currentFormControl?.disable();
        }
      }

      relatedControl.valueChanges.pipe(takeUntilDestroyed(this.destroy$)).subscribe(value => {
        if (!this.optionFilter) {
          this.optionFilter = this.initOptionFilter();
        }
        this.optionFilter[paramName] = paramConfig.valueMapper
          ? paramConfig.valueMapper(value)
          : value;
        if (value == null || value === '') {
          if (paramConfig.required) {
            this.currentFormControl?.disable();
            this.currentFormControl?.reset();
          }
        } else {
          this.currentFormControl?.enable();
        }
      });
    });
  }

  onSelectOpened(isOpen: boolean): void {
    if (isOpen) {
      this.optionFilter = this.initOptionFilter();
      this.fetchOptions();
    }
  }

  // Fetch options from the server
  private fetchOptions(): void {
    if (!this.requestUrl || this.isLoadingData) {
      this.options = this.definedOptions?.slice();
      return;
    }

    this.isLoadingData = true;
    this.cdr.detectChanges();
    this.loadOptions(this.optionFilter).subscribe(responseData => {
      this.options = responseData;
      this.isLoadingData = false;
      this.cdr.detectChanges();
    });
  }

  onSearch(searchText: string): void {
    if (!this.searchField) {
      return;
    }
    if (!this.optionFilter) {
      this.optionFilter = this.initOptionFilter();
    }
    // Implement your search logic here
    this.optionFilter[this.searchField] = searchText;

    if (this.paginationType === 'page') {
      this.optionFilter.page_index = 1;
    } else {
      this.optionFilter.offset = 0;
    }
    this.fetchOptions();
  }

  loadMore(): void {
    if (!this.pageable || this.isLoadingMore) return;

    if (!this.optionFilter) {
      this.optionFilter = this.initOptionFilter();
    }

    if (this.paginationType === 'page') {
      this.optionFilter.page_index = ((this.optionFilter?.page_index ?? 0) as number) + 1;
    } else {
      this.optionFilter.offset =
        ((this.optionFilter?.offset ?? 0) as number) + ((this.optionFilter?.limit ?? 0) as number);
    }

    this.isLoadingMore = true;
    this.cdr.detectChanges();

    this.loadOptions(this.optionFilter).subscribe(responseData => {
      this.options = [...(this.options ?? []), ...responseData];
      this.isLoadingMore = false;
      this.cdr.detectChanges();
    });
  }

  getUniqueKey(option: any, index: number): string | number {
    if (this.optionValueField && option[this.optionValueField]) {
      const originalValue = option[this.optionValueField];

      if (typeof originalValue === 'object' && originalValue !== null) {
        if ('uuid' in originalValue) return originalValue.uuid.value;
        return index;
      }

      const value = originalValue.toString().trim();
      return value || `empty_${index}`;
    }
    return index;
  }

  private loadOptions(filter: any): Observable<any[]> {
    return this.httpService
      .request(this.requestMethod, this.requestUrl!, filter, 'json', true)
      .pipe(
        map(response =>
          this.responseMapper
            ? this.responseMapper(response)
            : response?.value?.items ?? response?.value ?? response ?? [],
        ),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroy$),
      );
  }

  // Handles changes to the value
  onValueChange(value: any) {
    this.value = value;
    this.onChange(value);
    this.updateControlValidity(); // Ensure control state is updated
  }

  // Validator method
  override validate(control: AbstractControl): ValidationErrors | null {
    if (control && control?.value !== this.value) {
      control.setValue(this.value, { emitEvent: false, onlySelf: true });
    }
    this.control = control; // Keep a reference to the control
    return control.errors; // Return the errors from the control itself
  }

  readonly compareFn = (o1: any, o2: any): boolean => o1 === o2;
}
